import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaFilter, FaPlus } from 'react-icons/fa';
import PostsList from '../../components/socialContent/PostsList';
import useAuthStore from '../../store/authStore';
import { getPosts, deletePost } from '../../helpers/socialContentService';

const PostsHistoryPage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        platform: 'all',
        type: 'all',
        status: 'all',
        source: 'all' // 'auto' o 'manual'
    });

    const navigate = useNavigate();
    const { user } = useAuthStore();

    useEffect(() => {
        loadPosts();
    }, []);

    const loadPosts = async () => {
        try {
            setLoading(true);
            const data = await getPosts();
            setPosts(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (post) => {
        navigate(`/social-content/edit/${post._id}`);
    };

    const handleDelete = async (post) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este post?')) {
            try {
                await deletePost(post._id);
                await loadPosts();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    const handleDownload = (post) => {
        // Crear un enlace temporal para descargar la imagen
        const link = document.createElement('a');
        link.href = post.imageUrl;
        link.download = `post-${post._id}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const filteredPosts = posts.filter(post => {
        if (filters.platform !== 'all' && post.platform !== filters.platform) return false;
        if (filters.type !== 'all' && post.type !== filters.type) return false;
        if (filters.status !== 'all' && post.status !== filters.status) return false;
        if (filters.source !== 'all' && post.isAutoGenerated !== (filters.source === 'auto')) return false;
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <FaSpinner className="animate-spin text-blue-500 text-4xl" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900">
                    Historial de Posts
                </h1>
                <button
                    onClick={() => navigate('/social-content')}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                    <FaPlus />
                    <span>Crear Nuevo Post</span>
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {/* Filtros */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex items-center space-x-2 mb-4">
                    <FaFilter className="text-gray-500" />
                    <h2 className="text-lg font-semibold">Filtros</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <select
                        value={filters.platform}
                        onChange={(e) => handleFilterChange('platform', e.target.value)}
                        className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                        <option value="all">Todas las plataformas</option>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="twitter">Twitter</option>
                    </select>

                    <select
                        value={filters.type}
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                        className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                        <option value="all">Todos los tipos</option>
                        <option value="post">Post</option>
                        <option value="story">Story</option>
                        <option value="carousel">Carousel</option>
                    </select>

                    <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                        <option value="all">Todos los estados</option>
                        <option value="approved">Aprobados</option>
                        <option value="pending">Pendientes</option>
                    </select>

                    <select
                        value={filters.source}
                        onChange={(e) => handleFilterChange('source', e.target.value)}
                        className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                        <option value="all">Todas las fuentes</option>
                        <option value="auto">Auto-generados</option>
                        <option value="manual">Manuales</option>
                    </select>
                </div>
            </div>

            {/* Lista de Posts */}
            <PostsList
                posts={filteredPosts}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDownload={handleDownload}
            />
        </div>
    );
};

export default PostsHistoryPage; 